/**
 * Prescription Generator Utility
 * Generates a printable HTML prescription document
 */

import type { StructuredMedication } from '@/lib/utils/medications';
import type { PrescribedMedicine } from '@/ayurveda/types/ayurveda';
import { splitCommaSeparatedText } from '@/lib/utils/clinicalText';

export interface PrescriptionPatientInfo {
  name: string;
  phone?: string;
  identifier: string;
  age?: number;
  gender?: string;
  dateOfBirth?: string;
  maritalStatus?: string;
  address?: string;
}

export interface PrescriptionVitals {
  height?: number; // cm
  weight?: number; // kg
  temperature?: number; // F
  bloodPressure?: string;
  heartRate?: number;
  respiratoryRate?: number;
  oxygenSaturation?: number;
  hemoglobin?: number; // g/dL
  cholesterol?: number; // mg/dL
  bloodSugar?: number; // mg/dL (Glucose)
}

export interface PrescriptionDiagnosisItem {
  symptom: string;
  code: string;
  description?: string;
}

export interface PrescriptionData {
  /** Page size for the printed prescription. Defaults to 'A4' when omitted. */
  pageSize?: 'A4' | 'A5';
  patient: PrescriptionPatientInfo;
  date: string;
  visitNumber?: number;
  doctorName?: string;
  vitals?: PrescriptionVitals;
  complaints?: string;
  diagnosis?: string;
  diagnosisCodes?: PrescriptionDiagnosisItem[];
  medications: StructuredMedication[];
  // Ayurveda Treatment > Medicines rows, passed through unchanged so the
  // prescription prints the same 8 columns as the Case Sheet table (Anupana,
  // Timing and Additional Info have no StructuredMedication equivalent).
  // When any row has a name, this replaces `medications` entirely.
  ayurvedaMedicines?: PrescribedMedicine[];
  investigations?: string[];
  doctorNotes?: string;
}

/**
 * Generate HTML content for prescription
 */
export function generatePrescriptionHTML(data: PrescriptionData): string {
  const v = data.vitals || {};
  // Clamp to the supported values at runtime — data.pageSize is TS-only typed,
  // so a caller could still pass an arbitrary string through at runtime, which
  // would otherwise get interpolated straight into the @page CSS rule below.
  const pageSize: 'A4' | 'A5' = data.pageSize === 'A5' ? 'A5' : 'A4';
  const compact = pageSize === 'A5';
  const pagePadding = compact ? '6mm' : '10mm';

  // Build medication rows — mirrors the Medication table in the case sheet view
  // (Medicine/Type/Composition, Dosage & Strength/Route, Schedule, Duration, Meal, Instructions)
  const scheduleSlotLabels: Record<number, string[]> = {
    2: ['Morning', 'Night'],
    3: ['Morning', 'Noon', 'Night'],
    4: ['Morning', 'Noon', 'Evening', 'Night'],
  };

  const ayurvedaMedicines = (data.ayurvedaMedicines ?? []).filter((med) => med.name?.trim());
  const isAyurveda = ayurvedaMedicines.length > 0;

  let medicationsRows = '';
  ayurvedaMedicines.forEach((med, index) => {
    const cell = (value?: string | null) => escapeHtml(value?.trim() || '—');
    medicationsRows += `
        <tr>
          <td class="med-index">${index + 1}</td>
          <td class="med-name-cell"><span class="med-name">${escapeHtml(med.name?.trim() || '')}</span></td>
          <td>${cell(med.form)}</td>
          <td>${cell(med.dose)}</td>
          <td>${cell(med.frequency)}</td>
          <td>${cell(med.duration)}</td>
          <td>${cell(med.anupana)}</td>
          <td>${cell(med.timing)}</td>
          <td>${cell(med.additional_info)}</td>
        </tr>`;
  });

  (isAyurveda ? [] : data.medications).forEach((med, index) => {
    const isSos =
      med.dosingType?.toLowerCase() === 'sos' || med.duration?.trim().toUpperCase() === 'SOS';

    const nameCell = [
      `<span class="med-name">${escapeHtml(med.medication || '—')}</span>`,
      med.medicineType ? `<span class="med-type">${escapeHtml(med.medicineType)}</span>` : '',
      med.composition
        ? `<span class="med-composition">Composition:<br>${escapeHtml(med.composition)}</span>`
        : '',
    ]
      .filter(Boolean)
      .join('<br>');

    const dosageCell = [escapeHtml(med.dosage || '—'), med.route ? escapeHtml(med.route) : '']
      .filter(Boolean)
      .join('<br>');

    let scheduleCell: string;
    if (isSos) {
      scheduleCell = [
        '<span class="med-sos-label">SOS Medicine</span>',
        med.sosTrigger ? `Trigger&nbsp;&nbsp;: ${escapeHtml(med.sosTrigger)}` : '',
        med.maxPerDay ? `Max/Day&nbsp;: ${escapeHtml(med.maxPerDay)}` : '',
      ]
        .filter(Boolean)
        .join('<br>');
    } else {
      const slots = med.frequency?.trim().split('-') || [];
      const labels = scheduleSlotLabels[slots.length];
      scheduleCell =
        labels && slots.every((s) => /^\d+$/.test(s.trim()))
          ? labels
              .map((label, i) => `${label}&nbsp;&nbsp;: ${escapeHtml(slots[i].trim())}`)
              .join('<br>')
          : escapeHtml(med.frequency || '—');
    }

    medicationsRows += `
        <tr>
          <td class="med-index">${index + 1}</td>
          <td class="med-name-cell">${nameCell}</td>
          <td>${dosageCell}</td>
          <td>${scheduleCell}</td>
          <td>${escapeHtml(med.duration || '—')}</td>
          <td>${escapeHtml(med.relationWithMeal || '—')}</td>
          <td>${escapeHtml(med.additionalInfo || '—')}</td>
        </tr>`;
  });

  // Wraps pre-built <li> HTML in the bullet list — but only when there's at
  // least one real item. An empty field renders as a plain "-" instead of a
  // bullet with a dash next to it, matching how every other empty field on
  // the prescription (e.g. "-" for a missing vital) has no bullet marker.
  const renderBulletBox = (listItemsHtml: string[]): string =>
    listItemsHtml.length > 0 ? `<ul class="notes-list">${listItemsHtml.join('')}</ul>` : '-';

  // Diagnosis content shown in the summary box — ICD-11 codes (one bullet per
  // code) when present, otherwise the free-text diagnosis split into one
  // bullet per entry (e.g. "Ulcer, Angina, Gastroesophageal reflux disease").
  let diagnosisContent: string;
  if (data.diagnosisCodes && data.diagnosisCodes.length > 0) {
    diagnosisContent = renderBulletBox(
      data.diagnosisCodes.map((item) => {
        const code = item.description ? `${item.code} — ${item.description}` : item.code;
        return `<li>${escapeHtml(item.symptom)} (${escapeHtml(code)})</li>`;
      })
    );
  } else {
    const diagnosisItems = splitCommaSeparatedText(data.diagnosis);
    diagnosisContent = renderBulletBox(
      diagnosisItems.map((item) => `<li>${sanitizeInlineHtml(item)}</li>`)
    );
  }

  // Doctor's notes rendered as a bullet list — one bullet per line
  const notesItems = (data.doctorNotes || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const notesList = renderBulletBox(
    notesItems.map((line) => `<li>${sanitizeInlineHtml(line)}</li>`)
  );

  // Investigations rendered as a bullet list, same as Doctor's Notes — one bullet per entry
  const investigationItems = (data.investigations || []).map((item) => item.trim()).filter(Boolean);
  const investigationsList = renderBulletBox(
    investigationItems.map((item) => `<li>${sanitizeInlineHtml(item)}</li>`)
  );

  // Chief Complaint rendered as a bullet list, same as Investigations —
  // splits the comma-joined value (e.g. "Fever, chest pain, and cough for
  // 5 days") into one bullet per complaint.
  const complaintItems = splitCommaSeparatedText(data.complaints);
  const complaintsList = renderBulletBox(
    complaintItems.map((item) => `<li>${sanitizeInlineHtml(item)}</li>`)
  );

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Prescription - ${escapeHtml(data.patient.name)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.5;
      color: #000;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      font-size: 13px;
    }
    @media screen {
      .letterpad-header {
        height: 16px; /* screen preview approximation */
      }
      .letterpad-footer {
        height: 70px; /* screen preview approximation */
      }
    }

    /* ---- Section title ---- */
    .section-subtitle {
      font-size: 17px;
      font-weight: 700;
      color: #000;
      margin: 16px 0 8px;
    }
    .field-label {
      font-weight: 700;
      white-space: nowrap;
      color: #000;
    }

    /* ---- Doctor / Patient box ---- */
    .top-grid {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 12px;
    }
    .top-grid td.top-cell {
      width: 50%;
      padding: 4px 16px;
      vertical-align: top;
    }
    .top-grid td.top-cell:first-child {
      padding-left: 0;
      border-right: 1px solid #ccc;
    }
    .box-label {
      font-size: 12px;
      font-weight: 700;
      color: #666;
      text-transform: none;
      margin-bottom: 6px;
    }
    .box-name {
      font-size: 16px;
      font-weight: 700;
      margin-bottom: 8px;
    }
    .box-row {
      margin: 3px 0;
    }
    .box-row .field-label {
      display: inline-block;
      min-width: 100px;
    }

    /* ---- Vitals table ---- */
    .vitals-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
      table-layout: fixed;
      border: 1px solid #ddd;
    }
    .vitals-table th {
      text-align: left;
      font-weight: 700;
      font-size: 12px;
      color: #444;
      padding: 8px 10px;
      background: #f7f7f8;
      border-bottom: 1px solid #ddd;
      border-right: 1px solid #eee;
    }
    .vitals-table td {
      padding: 8px 10px;
      border-right: 1px solid #eee;
    }
    .vitals-table th:last-child,
    .vitals-table td:last-child {
      border-right: none;
    }

    /* ---- Chief Complaint / Diagnosis / Investigations box ---- */
    .triple-box {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
      border: 1px solid #ccc;
    }
    .triple-box td {
      width: 33.33%;
      vertical-align: top;
      padding: 10px 12px;
    }
    .triple-box td + td {
      border-left: 1px solid #ccc;
    }
    .triple-box .box-label {
      color: #000;
      font-size: 13px;
      margin-bottom: 6px;
    }

    /* ---- Medications table ---- */
    .med-section-title {
      text-align: center;
      font-size: 18px;
      font-weight: 700;
      color: #000;
      margin: 16px 0 8px;
    }
    .med-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
    }
    .med-table th {
      text-align: left;
      font-weight: 700;
      font-size: 12px;
      color: #666;
      padding: 8px 8px;
      border-bottom: 1px solid #ccc;
    }
    .med-table td {
      padding: 12px 8px;
      border-bottom: 1px solid #eee;
      text-align: left;
      vertical-align: top;
    }
    .med-table th.med-index,
    .med-table td.med-index {
      width: 24px;
      color: #666;
    }
    .med-name {
      font-weight: 700;
    }
    .med-type {
      color: #444;
    }
    .med-composition {
      font-size: 11px;
      font-weight: 400;
      color: #666;
    }
    .med-sos-label {
      font-weight: 700;
    }

    /* ---- Notes / Signature ---- */
    .notes-signature-grid {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
      border: 1px solid #ccc;
    }
    .notes-signature-grid td {
      width: 50%;
      vertical-align: top;
      padding: 12px 16px;
    }
    .notes-signature-grid td.notes-cell {
      border-right: 1px solid #ccc;
    }
    .notes-signature-grid .field-label {
      text-decoration: underline;
      font-size: 16px;
      color: #000;
      margin-bottom: 8px;
      display: block;
    }
    .notes-list {
      list-style: disc;
      padding-left: 18px;
    }
    .notes-list li {
      margin-bottom: 4px;
    }
    .signature-cell {
      text-align: center;
    }
    .signature-space {
      height: 40px;
    }
    .signature-name {
      font-style: italic;
      font-size: 18px;
      border-top: 1px solid #999;
      display: inline-block;
      padding-top: 4px;
      margin-top: 4px;
    }

    /* Print rules declared LAST so they win the cascade tie-break over the
       unconditional rules above (equal selector specificity; later wins). */
    @media print {
      body { margin: 0; padding: 0; }
      .no-print { display: none !important; }
      @page { size: ${pageSize} portrait; margin: 0; }
      body { padding: ${pagePadding}; }
      .letterpad-header { height: ${compact ? '3mm' : '8mm'}; } /* reserved for clinic/hospital letterpad header */
      .letterpad-footer { height: ${compact ? '8mm' : '25mm'}; } /* reserved for clinic/hospital letterpad footer */
      /* Ensure background colors/graphics print */
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      ${
        compact
          ? `
      /* ---- A5 compact mode: shrink type/spacing so content fits one page ---- */
      body { font-size: 10px; line-height: 1.3; }
      .section-subtitle { font-size: 13px; margin: 8px 0 4px; }
      .top-grid { margin-bottom: 6px; }
      .top-grid td.top-cell { padding: 2px 8px; }
      .box-label { font-size: 9px; margin-bottom: 3px; }
      .box-name { font-size: 12px; margin-bottom: 4px; }
      .box-row { margin: 1px 0; }
      .box-row .field-label { min-width: 70px; }
      .vitals-table { margin-bottom: 8px; }
      .vitals-table th, .vitals-table td { padding: 4px 6px; font-size: 9px; }
      .triple-box { margin-bottom: 8px; }
      .triple-box td { padding: 5px 6px; }
      .triple-box .box-label { font-size: 10px; margin-bottom: 3px; }
      .med-section-title { font-size: 14px; margin: 8px 0 4px; }
      .med-table { margin-bottom: 12px; }
      .med-table th { padding: 4px 4px; font-size: 9px; }
      .med-table td { padding: 5px 4px; }
      .med-composition { font-size: 9px; }
      .notes-signature-grid { margin-top: 8px; }
      .notes-signature-grid td { padding: 6px 8px; }
      .notes-signature-grid .field-label { font-size: 12px; margin-bottom: 4px; }
      .notes-list { padding-left: 14px; }
      .notes-list li { margin-bottom: 2px; }
      .signature-space { height: 20px; }
      .signature-name { font-size: 13px; }
      `
          : ''
      }
    }
  </style>
</head>
<body>

  <!-- LETTERPAD HEADER SPACE -->
  <div class="letterpad-header"></div>

  <!-- DOCTOR / PATIENT -->
  <table class="top-grid">
    <tr>
      <td class="top-cell">
        <div class="box-label">Doctor</div>
        <div class="box-name">${escapeHtml(data.doctorName || '')}</div>
      </td>
      <td class="top-cell">
        <div class="box-label">Patient</div>
        <div class="box-name">${escapeHtml(data.patient.name || '')}</div>
        <div class="box-row"><span class="field-label">Age / Gender :</span> ${[data.patient.age != null ? `${data.patient.age} Years` : '', data.patient.gender ? escapeHtml(data.patient.gender) : ''].filter(Boolean).join(' / ') || '-'}</div>
        <div class="box-row"><span class="field-label">Phone :</span> ${escapeHtml(data.patient.phone || '')}</div>
        <div class="box-row"><span class="field-label">Patient ID :</span> ${escapeHtml(data.patient.identifier || '')}</div>
      </td>
    </tr>
  </table>

  <!-- VITAL SIGNS -->
  <div class="section-subtitle">Vitals</div>

  <table class="vitals-table">
    <tr>
      <th>Height</th>
      <th>Weight</th>
      <th>Temperature</th>
      <th>SpO₂</th>
    </tr>
    <tr>
      <td>${v.height ? `${v.height} cm` : '-'}</td>
      <td>${v.weight ? `${v.weight} kg` : '-'}</td>
      <td>${v.temperature ? `${v.temperature} °F` : '-'}</td>
      <td>${v.oxygenSaturation ? `${v.oxygenSaturation} %` : '-'}</td>
    </tr>
    <tr>
      <th>Blood Pressure</th>
      <th>Hemoglobin</th>
      <th>Cholesterol</th>
      <th>Glucose</th>
    </tr>
    <tr>
      <td>${v.bloodPressure ? `${v.bloodPressure} mmHg` : '-'}</td>
      <td>${v.hemoglobin ? `${v.hemoglobin} g/dL` : '-'}</td>
      <td>${v.cholesterol ? `${v.cholesterol} mg/dL` : '-'}</td>
      <td>${v.bloodSugar ? `${v.bloodSugar} mg/dL` : '-'}</td>
    </tr>
  </table>

  <!-- Chief Complaint / Diagnosis / Investigations -->
  <table class="triple-box">
    <tr>
      <td>
        <div class="box-label">Chief Complaint</div>
        ${complaintsList}
      </td>
      <td>
        <div class="box-label">Diagnosis</div>
        ${diagnosisContent}
      </td>
      <td>
        <div class="box-label">Investigations</div>
        ${investigationsList}
      </td>
    </tr>
  </table>

  <!-- Medications -->
  <div class="med-section-title">Medications</div>

  <table class="med-table">
    <thead>
      <tr>
        <th class="med-index">#</th>
        ${
          isAyurveda
            ? `<th>Medicine</th>
        <th>Form</th>
        <th>Dosage</th>
        <th>Frequency</th>
        <th>Duration</th>
        <th>Anupana</th>
        <th>Timing</th>
        <th>Additional Info</th>`
            : `<th>Medicine</th>
        <th>Dosage &amp; Strength</th>
        <th>Schedule</th>
        <th>Duration</th>
        <th>Meal</th>
        <th>Instructions</th>`
        }
      </tr>
    </thead>
    <tbody>
      ${medicationsRows}
    </tbody>
  </table>

  <!-- Doctor's Notes / Signature -->
  <table class="notes-signature-grid">
    <tr>
      <td class="notes-cell">
        <span class="field-label">Doctor's Notes / Advice</span>
        ${notesList}
      </td>
      <td class="signature-cell">
        <span class="field-label">Doctor's Signature</span>
        <div class="signature-space"></div>
        <div class="signature-name">${escapeHtml(data.doctorName || '')}</div>
      </td>
    </tr>
  </table>

  <!-- LETTERPAD FOOTER SPACE -->
  <div class="letterpad-footer"></div>

  <div class="no-print" style="margin-top: 30px; text-align: center;">
    <button onclick="window.print()" style="padding: 10px 30px; font-size: 16px; cursor: pointer; background: #051858; color: #fff; border: none; border-radius: 4px;">
      Print Prescription
    </button>
  </div>
</body>
</html>
  `.trim();
}

/**
 * Escape HTML special characters using the browser's built-in DOM encoding
 */
function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Sanitize free text that may contain inline emphasis markup (e.g. <b>...</b>)
 * from the backend. Strips everything except a small allow-list of formatting
 * tags so emphasis renders, while still preventing arbitrary HTML injection.
 */
function sanitizeInlineHtml(str: string): string {
  if (!str) return '';
  const doc = new DOMParser().parseFromString(str, 'text/html');
  const allowedTags = new Set(['B', 'STRONG', 'I', 'EM', 'BR']);

  const clean = (node: ChildNode) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        if (!allowedTags.has(el.tagName)) {
          // Replace disallowed elements with their text content, keeping any nested content
          const text = doc.createTextNode(el.textContent || '');
          el.replaceWith(text);
          return;
        }
        // Strip all attributes from allowed tags
        Array.from(el.attributes).forEach((attr) => el.removeAttribute(attr.name));
        clean(el);
      }
    });
  };

  clean(doc.body);
  return doc.body.innerHTML;
}

/**
 * Open prescription in new window for printing
 */
export function printPrescription(data: PrescriptionData): void {
  const html = generatePrescriptionHTML(data);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}
